/**
 * NNADD — núcleo de desenho de mapas do configurador (U3, KTD2).
 *
 * Um só módulo para a página (pré-visualização) e para `tools/coordinates`
 * (ficheiro impresso), para que o que o cliente aprova seja o que é impresso
 * (R3). Sem imports e sem pedidos de rede: recebe geometria já carregada e
 * devolve caminhos SVG em milímetros. O browser carrega-o com `import()` e o
 * Node importa-o por caminho relativo.
 *
 * Uma só tinta (KTD7): a saída são formas, não cores. Cada camada diz se é
 * traço (com espessura em mm) ou cheio; `mapaParaSvg` pinta tudo na tinta dada.
 *
 * ===== Contrato de entrada
 *
 * `desenharMapa(pedido)`, com:
 * - `estilo`: um de `ESTILOS` — 'ruas', 'edificios', 'agua', 'relevo',
 *   'contorno', 'duas_terras'.
 * - `forma`: `{ tipo: 'circulo', diametroMm }` ou
 *   `{ tipo: 'retangulo', larguraMm, alturaMm }`. As coordenadas de saída vão
 *   de (0, 0), canto superior esquerdo, a (largura, altura), em mm.
 * - `centro`: `{ lat, lon }`, o ponto escolhido (todos os estilos menos
 *   'duas_terras'). O mapa é centrado nele.
 * - `raioM`: metros do centro até à borda da forma (o raio do círculo, ou
 *   metade do lado menor do retângulo). Por omissão `RAIOS_M[estilo]`.
 * - `pontos`: `[origem, destino]`, cada um `{ lat, lon }` (só 'duas_terras').
 * - `opcoes.espessuraMinimaMm`: o traço mais fino permitido (por omissão 0,3).
 * - `dados`: a geometria já carregada, sempre em [lon, lat] (graus WGS84):
 *   - `ruas`: `[{ classe, pontos: [[lon, lat], ...] }]`. A classe é a do
 *     OpenMapTiles ('motorway', 'trunk', 'primary', 'secondary', 'tertiary',
 *     'minor', 'service', 'track', 'path', ...) ou a etiqueta `highway` do OSM
 *     ('residential', 'unclassified', 'living_street', 'pedestrian',
 *     'footway', 'steps', ...). As ruas de serviço e as classes que não estão
 *     em `ESPESSURAS_RUAS` (comboio, ferry, ...) ficam de fora, como no
 *     protótipo.
 *   - `edificios`: polígonos, cada um uma lista de anéis `[[lon, lat], ...]`;
 *     o primeiro anel é o exterior, os outros são buracos (o sentido dos
 *     anéis não interessa: o módulo acerta-o). Um multipolígono do vector tile
 *     chega como vários polígonos.
 *   - `agua`: polígonos de água no mesmo formato (rios largos, lagos e o mar;
 *     no OpenMapTiles o mar é água, não há camada de costa à parte). Piscinas
 *     ficam de fora no carregador.
 *   - `rios`: `[{ classe, pontos }]`, linhas de água ('river', 'canal',
 *     'stream', 'brook'); as outras classes ficam de fora.
 *   - `grelha` (relevo): `{ largura, altura, valores, oeste, este, norte, sul,
 *     linhas }`. `valores` tem `largura × altura` altitudes em metros, por
 *     linhas, a primeira linha a norte. Os limites são as bordas exteriores da
 *     grelha e cada valor é o centro da sua célula: a coluna i fica em
 *     `oeste + (este − oeste) × (i + 0,5) / largura`. `linhas: 'mercator'` (por
 *     omissão, como nos tiles Terrarium) quer dizer que as linhas são
 *     equidistantes em y de Mercator; `'latitude'`, equidistantes em latitude.
 *     Altitudes ≤ 0 contam como o nível do mar.
 *   - `regiao` (contorno): a geometria GeoJSON (`Polygon` ou `MultiPolygon`)
 *     do Nominatim (KTD13), com as ilhas.
 *   - `costa` (duas_terras): linhas `[[lon, lat], ...]`, os contornos da água
 *     (mar e lagos) dos tiles de zoom baixo, já sem as bordas dos tiles
 *     (`costaDosTiles` em `assets/nnadd-mapas-tiles.js`). Pode vir vazia
 *     (dois lugares no interior); se não vier, o mapa fica indisponível (R20).
 *
 * ===== Saída
 *
 * `{ estilo, disponivel, motivo, vazio, contagem, larguraMm, alturaMm, camadas,
 * ... }`:
 * - `disponivel: false` com `motivo` — 'latitude' (|lat| > 85°, KTD12),
 *   'sem_dados' (a geometria pedida não veio: R20), 'sem_regiao' (não há
 *   polígono para o contorno, KTD13), 'pontos_em_falta', 'pontos_proximos'
 *   (a menos de 1 km, KTD5) ou 'pontos_antipodas'. Nesse caso não há camadas.
 * - `contagem`: os elementos desenhados dentro da forma (ruas, edifícios,
 *   águas e rios, curvas de nível, anéis do contorno, peças do arco).
 * - `vazio`: a contagem ficou abaixo de `MINIMOS[estilo]` (KTD12), ou a água
 *   cobre a forma quase toda. A página e o PC tratam um mapa vazio como
 *   indisponível e sugerem "só coordenadas".
 * - `camadas`: `[{ nome, modo: 'traco' | 'cheio', d, espessuraMm?, recorte? }]`,
 *   por ordem de desenho. `d` é um caminho SVG só com M, L e Z, em mm, com duas
 *   casas decimais. `recorte` (só no contorno) é `{ d, regra: 'evenodd' }`: o
 *   traço só aparece dentro dele, que é o exterior do polígono.
 * - relevo: também `niveis` (as altitudes das curvas) e `intervaloM`.
 * - duas_terras: também `arco: { distanciaKm, pecas, extensaoLonGraus,
 *   extremos, apice, normal, raioPontoMm }`, em mm dentro da forma: as duas
 *   pontas (origem primeiro), o ponto mais alto do arco e a direção para onde
 *   ele curva (unitária). O desenho da peça (U5) põe os nomes, as coordenadas
 *   e a distância a partir daqui.
 *
 * ===== As duas terras (KTD5)
 *
 * Os dois pontos ficam na posição real, numa projeção equirretangular à volta
 * do meio dos dois (com o fator de latitude), sobre a costa a traço fino
 * (`ESPESSURA_COSTA_MM`). Ligam-se por um arco de círculo grande no papel, com
 * a flecha proporcional à corda (`FLECHA_ARCO`), que curva para cima. O
 * enquadramento sai só dos pontos e da forma: o arco e as pontas ocupam a
 * forma tirando as margens (`MARGENS_DUAS_TERRAS`, em mm; a de baixo é para
 * os nomes e as coordenadas), e a costa enche a forma toda. `formaDuasTerras`
 * dá a moldura que o configurador usa: 120 mm de altura e a largura que o
 * arco pede, entre 160 e 240 mm. `caixaDuasTerras` dá a
 * caixa em graus que esse enquadramento mostra, para o carregador pedir os
 * tiles. O meridiano central é o do meio dos dois pontos: um par como
 * Fiji → Samoa, de um lado e do outro dos 180°, sai inteiro.
 *
 * O mesmo pedido dá sempre o mesmo resultado, byte a byte.
 */

export const VERSAO_MAPAS = 'mapas-2';

export const ESTILOS = Object.freeze(['ruas', 'relevo', 'contorno', 'duas_terras', 'edificios', 'agua']);

/** Acima disto (em graus, norte ou sul) os estilos de mapa ficam indisponíveis (KTD12). */
export const LATITUDE_MAXIMA = 85;
/** Duas terras: abaixo disto os dois pontos são o mesmo lugar (KTD5). */
export const DISTANCIA_MINIMA_M = 1000;

/** Raio por omissão, em metros (Assumptions: 600 m; U10 pode reduzir). */
export const RAIOS_M = Object.freeze({ ruas: 600, edificios: 600, agua: 600, relevo: 3000 });

/** Elementos mínimos dentro da forma para o mapa não contar como vazio (KTD12). */
export const MINIMOS = Object.freeze({ ruas: 10, edificios: 10, agua: 1, relevo: 1, contorno: 1, duas_terras: 1 });

/**
 * Espessura das ruas por classe, em fração do lado menor da forma. Os valores
 * vêm do protótipo `a-ruas.svg` (7, 5, 4 e 2 px num círculo de 1000 px).
 */
export const ESPESSURAS_RUAS = Object.freeze({
  motorway: 0.008,
  trunk: 0.008,
  primary: 0.007,
  secondary: 0.007,
  tertiary: 0.005,
  minor: 0.004,
  residential: 0.004,
  unclassified: 0.004,
  living_street: 0.004,
  road: 0.004,
  pedestrian: 0.004,
  busway: 0.004,
  track: 0.002,
  path: 0.002,
  footway: 0.002,
  steps: 0.002,
  cycleway: 0.002,
  bridleway: 0.002,
});

const ESPESSURAS_RIOS = Object.freeze({ river: 0.006, canal: 0.004, stream: 0.0025, brook: 0.0025 });
const ESPESSURAS_RIOS_RELEVO = Object.freeze({ river: 0.0045, canal: 0.003, stream: 0.002, brook: 0.002 });
const ESPESSURA_CURVA = 0.0015;
const ESPESSURA_CURVA_PRINCIPAL = 0.003;
const ESPESSURA_CONTORNO = 0.008;
const ESPESSURA_MINIMA_MM = 0.3;

// ===== As duas terras (KTD5)
/** Flecha do arco a dividir pela corda: o arco é mais alto quanto mais longe forem os pontos. */
export const FLECHA_ARCO = 0.28;
/** A costa: o traço mais fino que se imprime, para ficar leve ao pé do arco (uma só tinta, sem transparência). */
export const ESPESSURA_COSTA_MM = 0.3;
/** Arco e pontos, em fração do lado menor da forma (1,02 mm e 2,5 mm numa forma de 120 mm). */
const ESPESSURA_ARCO = 0.0085;
const RAIO_PONTO = 0.021;
/**
 * Margens do arco dentro da forma retangular, em mm. A de baixo guarda o
 * lugar dos nomes e das coordenadas por baixo das pontas; as dos lados deixam
 * ver a costa à volta.
 */
export const MARGENS_DUAS_TERRAS = Object.freeze({ lados: 12, topo: 6, baixo: 29 });
/** A moldura de "as duas terras": altura fixa, largura entre o mínimo (para os nomes) e o máximo. */
export const MOLDURA_DUAS_TERRAS = Object.freeze({ alturaMm: 120, larguraMinMm: 160, larguraMaxMm: 240 });
/** Num círculo, o arco ocupa um quadrado central com este lado (em fração do diâmetro). */
const QUADRADO_CIRCULO = 0.62;
/** Troços de costa mais curtos do que isto (mm) são ruído: lagos minúsculos, pontas de tiles. */
const COSTA_MINIMA_MM = 1;
const TOLERANCIA_COSTA_MM = 0.1;
const TROCOS_ARCO = 96;

/** Tolerância da simplificação (Douglas-Peucker), em mm. */
const TOLERANCIA_MM = 0.05;
const TOLERANCIA_CONTORNO_MM = 0.1;
/** Relevo: número de curvas que se procura (~20) e o intervalo mínimo, em metros. */
const ALVO_CURVAS = 20;
const PASSO_MINIMO_M = 5;
/** Abaixo desta amplitude (m) a área é plana de mais para ter curvas. */
const AMPLITUDE_MINIMA_M = 1;
/** Abaixo deste intervalo (m) suaviza-se mais a grelha. */
const PASSO_RUIDOSO_M = 10;
/** Uma curva de nível fechada mais curta do que esta fração do lado menor é ruído. */
const CURVA_MINIMA = 0.03;
/** Água que cobre esta fração da forma é um mapa vazio (o mar alto). */
const AGUA_MAXIMA = 0.98;

const R_TERRA = 6371008.8;
const RAD = Math.PI / 180;

// ===== Disponibilidade

/** Para uma latitude, que estilos se podem desenhar (só a regra dos 85°). */
export function disponibilidadeEstilos(lat) {
  const ok = Number.isFinite(lat) && Math.abs(lat) <= LATITUDE_MAXIMA;
  return Object.fromEntries(ESTILOS.map((e) => [e, ok]));
}

/** Distância de círculo máximo entre dois pontos `{ lat, lon }`, em metros. */
export function distanciaM(a, b) {
  const dLat = (b.lat - a.lat) * RAD;
  const dLon = (b.lon - a.lon) * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * R_TERRA * Math.asin(Math.min(1, Math.sqrt(h)));
}

// ===== Números e caminhos

/** Duas casas decimais, sem zeros a mais e sem "-0". */
const n2 = (v) => {
  const r = Math.round(v * 100) / 100;
  return String(r === 0 ? 0 : r);
};

/** Uma polilinha (ou um anel, com `fechar`) em texto SVG; salta pontos repetidos. */
function caminho(pts, fechar) {
  let d = '';
  let ultimo = '';
  for (let i = 0; i < pts.length; i++) {
    const p = `${n2(pts[i][0])} ${n2(pts[i][1])}`;
    if (p === ultimo) continue;
    d += (d ? 'L' : 'M') + p;
    ultimo = p;
  }
  return fechar ? d + 'Z' : d;
}

const areaAssinada = (r) => {
  let s = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) s += r[j][0] * r[i][1] - r[i][0] * r[j][1];
  return s / 2;
};

/** Douglas-Peucker iterativo; mantém sempre o primeiro e o último ponto. */
function simplificar(pts, tol) {
  if (pts.length <= 2) return pts;
  const manter = new Uint8Array(pts.length);
  manter[0] = manter[pts.length - 1] = 1;
  const pilha = [[0, pts.length - 1]];
  const tol2 = tol * tol;
  while (pilha.length) {
    const [i0, i1] = pilha.pop();
    const [ax, ay] = pts[i0];
    const [bx, by] = pts[i1];
    const dx = bx - ax;
    const dy = by - ay;
    const l2 = dx * dx + dy * dy;
    let maior = -1;
    let k = -1;
    for (let i = i0 + 1; i < i1; i++) {
      const [px, py] = pts[i];
      let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const ex = ax + t * dx - px;
      const ey = ay + t * dy - py;
      const d2 = ex * ex + ey * ey;
      if (d2 > maior) {
        maior = d2;
        k = i;
      }
    }
    if (maior > tol2) {
      manter[k] = 1;
      pilha.push([i0, k], [k, i1]);
    }
  }
  return pts.filter((_, i) => manter[i]);
}

/** Chaikin: arredonda os cantos das curvas de nível, que saem da grelha aos degraus. */
function chaikin(pts, fechada, vezes) {
  let p = pts;
  for (let v = 0; v < vezes; v++) {
    const o = [];
    const n = fechada ? p.length - 1 : p.length;
    if (!fechada) o.push(p[0]);
    for (let i = 0; i < n - (fechada ? 0 : 1); i++) {
      const a = p[i];
      const b = p[(i + 1) % n];
      o.push([0.75 * a[0] + 0.25 * b[0], 0.75 * a[1] + 0.25 * b[1]], [0.25 * a[0] + 0.75 * b[0], 0.25 * a[1] + 0.75 * b[1]]);
    }
    if (fechada) o.push(o[0]);
    else o.push(p[p.length - 1]);
    p = o;
  }
  return p;
}

// ===== Forma e recorte

/** Um polígono regular de `lados` lados inscrito no círculo de centro (cx, cy) e raio r. */
function poligonoCircular(cx, cy, r, lados) {
  const pts = [];
  for (let i = 0; i < lados; i++) {
    const a = (i / lados) * 2 * Math.PI;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

/** Os polígonos de uma geometria GeoJSON (`Polygon` ou `MultiPolygon`); [] nas outras. */
const poligonosDe = (g) => (g?.type === 'Polygon' ? [g.coordinates] : g?.type === 'MultiPolygon' ? g.coordinates : []);

/** A forma da peça em mm: um polígono convexo para recortar (o círculo com 256 lados). */
function prepararForma(forma) {
  if (forma?.tipo === 'circulo' && forma.diametroMm > 0) {
    const D = forma.diametroMm;
    const r = D / 2;
    const poligono = poligonoCircular(r, r, r, 256);
    const rDentro = r * Math.cos(Math.PI / 256);
    return {
      tipo: 'circulo', largura: D, altura: D, cx: r, cy: r, raio: r, poligono,
      dentro: (p) => (p[0] - r) ** 2 + (p[1] - r) ** 2 <= r * r,
      dentroSeguro: (p) => (p[0] - r) ** 2 + (p[1] - r) ** 2 <= rDentro * rDentro,
    };
  }
  if (forma?.tipo === 'retangulo' && forma.larguraMm > 0 && forma.alturaMm > 0) {
    const W = forma.larguraMm;
    const H = forma.alturaMm;
    const dentro = (p) => p[0] >= 0 && p[0] <= W && p[1] >= 0 && p[1] <= H;
    return {
      tipo: 'retangulo', largura: W, altura: H, cx: W / 2, cy: H / 2, raio: Math.min(W, H) / 2,
      poligono: [[0, 0], [W, 0], [W, H], [0, H]], dentro, dentroSeguro: dentro,
    };
  }
  throw new Error('nnadd-mapas: forma inválida (circulo com diametroMm, ou retangulo com larguraMm e alturaMm)');
}

const cruz = (ax, ay, bx, by) => ax * by - ay * bx;

/** Sentido do polígono de recorte, para que "dentro" seja sempre o mesmo lado. */
function sinal(poly) {
  return areaAssinada(poly) >= 0 ? 1 : -1;
}

/** Recorta uma polilinha pelo polígono convexo (Cyrus-Beck); devolve as partes de dentro. */
function recortarLinha(pts, F) {
  const poly = F.poligono;
  const s = (F._sinal ??= sinal(poly));
  const partes = [];
  let atual = null;
  for (let i = 0; i + 1 < pts.length; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    let tE = 0;
    let tL = 1;
    if (!(F.dentroSeguro(p0) && F.dentroSeguro(p1))) {
      const dx = p1[0] - p0[0];
      const dy = p1[1] - p0[1];
      for (let j = 0; j < poly.length && tE <= tL; j++) {
        const a = poly[j];
        const b = poly[(j + 1) % poly.length];
        const ex = b[0] - a[0];
        const ey = b[1] - a[1];
        const num = s * cruz(ex, ey, p0[0] - a[0], p0[1] - a[1]);
        const den = s * cruz(ex, ey, dx, dy);
        if (den === 0) {
          if (num < 0) tE = 2;
        } else {
          const t = -num / den;
          if (den > 0) tE = Math.max(tE, t);
          else tL = Math.min(tL, t);
        }
      }
      if (tE > tL) {
        atual = null;
        continue;
      }
    }
    const a = tE > 0 ? [p0[0] + (p1[0] - p0[0]) * tE, p0[1] + (p1[1] - p0[1]) * tE] : p0;
    const b = tL < 1 ? [p0[0] + (p1[0] - p0[0]) * tL, p0[1] + (p1[1] - p0[1]) * tL] : p1;
    if (!atual || tE > 0) {
      atual = [a];
      partes.push(atual);
    }
    atual.push(b);
    if (tL < 1) atual = null;
  }
  return partes.filter((p) => p.length >= 2);
}

/** Recorta um anel pelo polígono convexo (Sutherland-Hodgman). */
function recortarAnel(anel, F) {
  if (anel.every(F.dentroSeguro)) return anel;
  const poly = F.poligono;
  const s = (F._sinal ??= sinal(poly));
  let saida = anel;
  for (let j = 0; j < poly.length && saida.length; j++) {
    const a = poly[j];
    const b = poly[(j + 1) % poly.length];
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    const lado = (p) => s * cruz(ex, ey, p[0] - a[0], p[1] - a[1]);
    const entrada = saida;
    saida = [];
    for (let i = 0; i < entrada.length; i++) {
      const p = entrada[i];
      const q = entrada[(i + 1) % entrada.length];
      const lp = lado(p);
      const lq = lado(q);
      if (lp >= 0) saida.push(p);
      if ((lp >= 0) !== (lq >= 0)) {
        const t = lp / (lp - lq);
        saida.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
      }
    }
  }
  return saida;
}

// ===== Projeção local

/** Equirretangular à volta do centro, com o fator de latitude; metros → mm. */
function projecaoLocal(centro, raioM, F) {
  const k = Math.cos(centro.lat * RAD);
  const s = (F.raio / raioM) * R_TERRA * RAD;
  return ([lon, lat]) => {
    let dl = lon - centro.lon;
    dl -= 360 * Math.round(dl / 360);
    return [F.cx + dl * k * s, F.cy - (lat - centro.lat) * s];
  };
}

const espessura = (frac, F, opcoes) => Math.max(frac * Math.min(F.largura, F.altura), opcoes.espessuraMinimaMm);

/** Linhas por classe → camadas de traço, uma por espessura; conta os elementos visíveis. */
function linhasPorClasse(linhas, tabela, nome, P, F, opcoes) {
  const porEspessura = new Map();
  let contagem = 0;
  for (const linha of linhas ?? []) {
    const frac = tabela[linha?.classe];
    if (!frac || !Array.isArray(linha.pontos) || linha.pontos.length < 2) continue;
    const pts = simplificar(linha.pontos.map(P), TOLERANCIA_MM);
    const partes = recortarLinha(pts, F);
    if (!partes.length) continue;
    contagem++;
    const e = espessura(frac, F, opcoes);
    porEspessura.set(e, (porEspessura.get(e) ?? '') + partes.map((p) => caminho(p, false)).join(''));
  }
  const camadas = [...porEspessura.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([e, d]) => ({ nome, modo: 'traco', espessuraMm: e, d }));
  return { contagem, camadas };
}

/**
 * Um anel em mm, simplificado, sem o ponto repetido do fim e recortado pela
 * forma: `{ r, a }` com a área assinada, ou null se não ficar nada visível.
 */
function anelRecortado(anel, P, F) {
  if (!Array.isArray(anel) || anel.length < 4) return null;
  let r = simplificar(anel.map(P), TOLERANCIA_MM);
  if (r.length > 1 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1]) r = r.slice(0, -1);
  r = recortarAnel(r, F);
  if (r.length < 3) return null;
  const a = areaAssinada(r);
  if (Math.abs(a) < 0.005) return null;
  return { r, a };
}

/** Polígonos (exterior + buracos) → anéis em mm, recortados, com o sentido acertado. */
function poligonosRecortados(poligonos, P, F) {
  const aneis = [];
  let contagem = 0;
  for (const poligono of poligonos ?? []) {
    if (!Array.isArray(poligono)) continue;
    let exterior = true;
    let visivel = false;
    for (const anel of poligono) {
      const eraExterior = exterior;
      exterior = false;
      const recortado = anelRecortado(anel, P, F);
      // um exterior que não se vê leva os buracos com ele
      if (!recortado) {
        if (eraExterior) break;
        continue;
      }
      let { r } = recortado;
      const { a } = recortado;
      // exterior com área positiva, buracos negativa: a regra nonzero tira os buracos
      // e não abre buracos onde dois polígonos se sobrepõem (bordas dos tiles)
      if ((a > 0) !== eraExterior) r = r.slice().reverse();
      aneis.push(r);
      if (eraExterior) visivel = true;
    }
    if (visivel) contagem++;
  }
  return { aneis, contagem };
}

/** Fração da forma coberta pelos anéis (amostragem numa grelha 41×41, regra nonzero). */
function fracaoCoberta(aneis, F) {
  if (!aneis.length) return 0;
  let dentro = 0;
  let cobertos = 0;
  for (let j = 0; j <= 40; j++) {
    for (let i = 0; i <= 40; i++) {
      const p = [(F.largura * (i + 0.5)) / 41, (F.altura * (j + 0.5)) / 41];
      if (!F.dentro(p)) continue;
      dentro++;
      let w = 0;
      for (const r of aneis) {
        for (let a = 0, b = r.length - 1; a < r.length; b = a++) {
          const [x0, y0] = r[b];
          const [x1, y1] = r[a];
          if (y0 <= p[1]) {
            if (y1 > p[1] && cruz(x1 - x0, y1 - y0, p[0] - x0, p[1] - y0) > 0) w++;
          } else if (y1 <= p[1] && cruz(x1 - x0, y1 - y0, p[0] - x0, p[1] - y0) < 0) w--;
        }
      }
      if (w !== 0) cobertos++;
    }
  }
  return dentro ? cobertos / dentro : 0;
}

// ===== Estilos

function ruas(pedido, F, opcoes) {
  const { ruas: linhas } = pedido.dados ?? {};
  if (!Array.isArray(linhas)) return { motivo: 'sem_dados' };
  const P = projecaoLocal(pedido.centro, pedido.raioM ?? RAIOS_M.ruas, F);
  return linhasPorClasse(linhas, ESPESSURAS_RUAS, 'ruas', P, F, opcoes);
}

function edificios(pedido, F) {
  const { edificios: polis } = pedido.dados ?? {};
  if (!Array.isArray(polis)) return { motivo: 'sem_dados' };
  const P = projecaoLocal(pedido.centro, pedido.raioM ?? RAIOS_M.edificios, F);
  const { aneis, contagem } = poligonosRecortados(polis, P, F);
  return { contagem, camadas: [{ nome: 'edificios', modo: 'cheio', d: aneis.map((r) => caminho(r, true)).join('') }] };
}

function agua(pedido, F, opcoes) {
  const { agua: polis, rios } = pedido.dados ?? {};
  if (!Array.isArray(polis) && !Array.isArray(rios)) return { motivo: 'sem_dados' };
  const P = projecaoLocal(pedido.centro, pedido.raioM ?? RAIOS_M.agua, F);
  const { aneis, contagem } = poligonosRecortados(polis, P, F);
  const linhas = linhasPorClasse(rios, ESPESSURAS_RIOS, 'rios', P, F, opcoes);
  const coberta = fracaoCoberta(aneis, F);
  return {
    contagem: contagem + linhas.contagem,
    cheia: coberta >= AGUA_MAXIMA,
    camadas: [{ nome: 'agua', modo: 'cheio', d: aneis.map((r) => caminho(r, true)).join('') }, ...linhas.camadas],
  };
}

/** Números redondos para o intervalo das curvas: 1, 2, 2,5 e 5 vezes uma potência de 10. */
function redondo(v, paraCima) {
  const p = 10 ** Math.floor(Math.log10(v));
  const passos = [1, 2, 2.5, 5, 10];
  if (paraCima) return p * passos.find((m) => m * p >= v - 1e-9);
  return p * [...passos].reverse().find((m) => m * p <= v + 1e-9);
}

/**
 * Intervalo adaptado à amplitude local: procura ~20 curvas, nunca abaixo de 5 m,
 * mas, se isso deixar a área sem curvas, desce até caber pelo menos uma.
 */
export function intervaloCurvas(minimo, maximo) {
  const amplitude = maximo - minimo;
  if (!(amplitude >= AMPLITUDE_MINIMA_M)) return 0;
  let passo = Math.max(redondo(amplitude / ALVO_CURVAS, true), PASSO_MINIMO_M);
  // com passo ≤ amplitude/2 há sempre um múltiplo estritamente entre o mínimo e o máximo
  if (passo > amplitude / 2) passo = redondo(amplitude / 2, false);
  return passo;
}

function relevo(pedido, F, opcoes) {
  const g = pedido.dados?.grelha;
  const W = g?.largura;
  const H = g?.altura;
  if (!g || !(W >= 2 && H >= 2) || g.valores?.length !== W * H) return { motivo: 'sem_dados' };
  const P = projecaoLocal(pedido.centro, pedido.raioM ?? RAIOS_M.relevo, F);

  // grelha → lon/lat (posições fracionárias, para os pontos interpolados nas arestas)
  const merc = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * RAD) / 2));
  const mercInv = (y) => (2 * Math.atan(Math.exp(y)) - Math.PI / 2) / RAD;
  const emMercator = (g.linhas ?? 'mercator') === 'mercator';
  const yN = emMercator ? merc(g.norte) : g.norte;
  const yS = emMercator ? merc(g.sul) : g.sul;
  const paraMm = (x, y) => {
    const lon = g.oeste + ((g.este - g.oeste) * (x + 0.5)) / W;
    const v = yN + ((yS - yN) * (y + 0.5)) / H;
    return P([lon, emMercator ? mercInv(v) : v]);
  };

  // nível do mar a 0 e uma passagem de suavização
  let z = suavizar(Float64Array.from(g.valores, (v) => (v > 0 ? v : 0)), W, H);

  // amplitude só dentro da forma
  let minimo = Infinity;
  let maximo = -Infinity;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!F.dentro(paraMm(x, y))) continue;
      const v = z[y * W + x];
      if (v < minimo) minimo = v;
      if (v > maximo) maximo = v;
    }
  }
  const passo = minimo <= maximo ? intervaloCurvas(minimo, maximo) : 0;
  // num lugar plano o intervalo é de poucos metros e o ruído do modelo de terreno
  // (~1 m) desenharia manchas: suaviza-se mais antes de traçar as curvas
  if (passo && passo < PASSO_RUIDOSO_M) for (let i = 0; i < 4; i++) z = suavizar(z, W, H);
  const niveis = [];
  if (passo) for (let k = Math.floor(minimo / passo) + 1; k * passo < maximo; k++) niveis.push(k);

  let normais = '';
  let principais = '';
  let contagem = 0;
  for (const k of niveis) {
    const nivel = k * passo;
    let d = '';
    for (const { pts, fechada } of curvasDeNivel(z, W, H, nivel)) {
      let mm = chaikin(pts, fechada, 2).map(([x, y]) => paraMm(x, y));
      if (fechada) {
        let comp = 0;
        for (let i = 1; i < mm.length; i++) comp += Math.hypot(mm[i][0] - mm[i - 1][0], mm[i][1] - mm[i - 1][1]);
        if (comp < CURVA_MINIMA * Math.min(F.largura, F.altura)) continue;
      }
      mm = simplificar(mm, TOLERANCIA_MM);
      for (const parte of recortarLinha(mm, F)) {
        d += caminho(parte, false);
        contagem++;
      }
    }
    if (k % 5 === 0) principais += d;
    else normais += d;
  }

  const camadas = [
    { nome: 'curvas', modo: 'traco', espessuraMm: espessura(ESPESSURA_CURVA, F, opcoes), d: normais },
    { nome: 'curvas_principais', modo: 'traco', espessuraMm: espessura(ESPESSURA_CURVA_PRINCIPAL, F, opcoes), d: principais },
  ];
  // os rios por cima, numa camada só (a mais grossa das classes presentes)
  const rios = linhasPorClasse(pedido.dados.rios, ESPESSURAS_RIOS_RELEVO, 'rios', P, F, opcoes);
  if (rios.camadas.length) {
    camadas.push({
      nome: 'rios', modo: 'traco',
      espessuraMm: Math.max(...rios.camadas.map((c) => c.espessuraMm)),
      d: rios.camadas.map((c) => c.d).join(''),
    });
  }
  return { contagem, camadas, niveis: niveis.map((k) => k * passo), intervaloM: passo };
}

/** Uma passagem de suavização binomial 3×3, com as bordas repetidas. */
function suavizar(z0, W, H) {
  const z = new Float64Array(W * H);
  const pesos = [1, 2, 1];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      let s = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = Math.min(H - 1, Math.max(0, y + dy));
        for (let dx = -1; dx <= 1; dx++) {
          const xx = Math.min(W - 1, Math.max(0, x + dx));
          s += z0[yy * W + xx] * pesos[dy + 1] * pesos[dx + 1];
        }
      }
      z[y * W + x] = s / 16;
    }
  }
  return z;
}

/**
 * Marching squares próprio (KTD2). Devolve polilinhas em coordenadas da grelha,
 * já encadeadas: cada ponto vive numa aresta da grelha, partilhada por duas
 * células, e é por essas arestas que os segmentos se ligam.
 */
function curvasDeNivel(z, W, H, nivel) {
  const pontos = new Map();
  const ponto = (chave) => {
    let p = pontos.get(chave);
    if (p) return p;
    const i = chave >> 1;
    const x = i % W;
    const y = (i - x) / W;
    const a = z[i];
    if (chave & 1) {
      const b = z[i + W];
      p = [x, y + (nivel - a) / (b - a)];
    } else {
      const b = z[i + 1];
      p = [x + (nivel - a) / (b - a), y];
    }
    pontos.set(chave, p);
    return p;
  };
  // chaves das arestas: horizontal (x,y)-(x+1,y) = 2i; vertical (x,y)-(x,y+1) = 2i+1
  const segs = [];
  const porAresta = new Map();
  const ligar = (c1, c2) => {
    const s = segs.length;
    segs.push([c1, c2]);
    for (const c of [c1, c2]) {
      const l = porAresta.get(c);
      if (l) l.push(s);
      else porAresta.set(c, [s]);
    }
  };
  for (let y = 0; y < H - 1; y++) {
    for (let x = 0; x < W - 1; x++) {
      const i = y * W + x;
      const a = z[i];
      const b = z[i + 1];
      const c = z[i + W + 1];
      const e = z[i + W];
      const caso = (a > nivel ? 1 : 0) | (b > nivel ? 2 : 0) | (c > nivel ? 4 : 0) | (e > nivel ? 8 : 0);
      if (caso === 0 || caso === 15) continue;
      const T = 2 * i;
      const Bm = 2 * (i + W);
      const L = 2 * i + 1;
      const R = 2 * (i + 1) + 1;
      const centroAlto = (a + b + c + e) / 4 > nivel;
      switch (caso) {
        case 1: case 14: ligar(L, T); break;
        case 2: case 13: ligar(T, R); break;
        case 3: case 12: ligar(L, R); break;
        case 4: case 11: ligar(R, Bm); break;
        case 6: case 9: ligar(T, Bm); break;
        case 7: case 8: ligar(L, Bm); break;
        case 5:
          if (centroAlto) { ligar(T, R); ligar(L, Bm); } else { ligar(L, T); ligar(R, Bm); }
          break;
        case 10:
          if (centroAlto) { ligar(L, T); ligar(R, Bm); } else { ligar(T, R); ligar(L, Bm); }
          break;
        default:
      }
    }
  }
  const usado = new Uint8Array(segs.length);
  const linhas = [];
  const seguir = (s0, inicio) => {
    const chaves = [inicio];
    let s = s0;
    let atual = inicio;
    while (s !== undefined && !usado[s]) {
      usado[s] = 1;
      const [c1, c2] = segs[s];
      atual = c1 === atual ? c2 : c1;
      chaves.push(atual);
      s = porAresta.get(atual).find((t) => !usado[t]);
    }
    const fechada = chaves.length > 2 && chaves[0] === chaves[chaves.length - 1];
    linhas.push({ pts: chaves.map(ponto), fechada });
  };
  // primeiro as abertas (que começam numa borda da grelha), depois os anéis
  for (let s = 0; s < segs.length; s++) {
    if (usado[s]) continue;
    for (const c of segs[s]) {
      if (porAresta.get(c).length === 1) {
        seguir(s, c);
        break;
      }
    }
  }
  for (let s = 0; s < segs.length; s++) if (!usado[s]) seguir(s, segs[s][0]);
  return linhas;
}

function contorno(pedido, F, opcoes) {
  const g = pedido.dados?.regiao;
  const aneisLL = (poligonosDe(g) ?? []).flat().filter((r) => Array.isArray(r) && r.length >= 4);
  if (!aneisLL.length) return { motivo: 'sem_regiao' };

  // projeção à volta do centro da caixa, e escala para caber na forma com o traço de fora
  let [o, e, s, n] = [Infinity, -Infinity, Infinity, -Infinity];
  for (const r of aneisLL) for (const [lon, lat] of r) {
    o = Math.min(o, lon); e = Math.max(e, lon); s = Math.min(s, lat); n = Math.max(n, lat);
  }
  const c = { lon: (o + e) / 2, lat: (s + n) / 2 };
  const k = Math.cos(c.lat * RAD);
  const metros = aneisLL.map((r) => r.map(([lon, lat]) => [(lon - c.lon) * k, c.lat - lat]));
  const esp = espessura(ESPESSURA_CONTORNO, F, opcoes);
  const folga = esp + 0.03 * Math.min(F.largura, F.altura);
  let escala;
  if (F.tipo === 'circulo') {
    let maior = 0;
    for (const r of metros) for (const [x, y] of r) maior = Math.max(maior, Math.hypot(x, y));
    escala = (F.raio - folga) / (maior || 1);
  } else {
    const lx = (e - o) * k || 1e-9;
    const ly = n - s || 1e-9;
    escala = Math.min((F.largura - 2 * folga) / lx, (F.altura - 2 * folga) / ly);
  }
  const aneis = [];
  for (const r of metros) {
    const mm = simplificar(r.map(([x, y]) => [F.cx + x * escala, F.cy + y * escala]), TOLERANCIA_CONTORNO_MM);
    if (mm.length >= 4 && Math.abs(areaAssinada(mm)) >= 0.05) aneis.push(mm);
  }
  const d = aneis.map((r) => caminho(r.slice(0, -1), true)).join('');
  const W = n2(F.largura);
  const H = n2(F.altura);
  // traço com o dobro da espessura, recortado pelo exterior do polígono: fica só a metade de fora
  return {
    contagem: aneis.length,
    camadas: [{
      nome: 'contorno', modo: 'traco', espessuraMm: 2 * esp, d,
      recorte: { d: `M0 0L${W} 0L${W} ${H}L0 ${H}Z${d}`, regra: 'evenodd' },
    }],
  };
}

/**
 * Parte uma linha [lon, lat] onde ela salta de um lado ao outro do
 * antimeridiano (KTD5), com os pontos de corte em ±180.
 */
export function cortarNoAntimeridiano(linha) {
  const pecas = [];
  let atual = [linha[0]];
  for (let i = 1; i < linha.length; i++) {
    const p = linha[i - 1];
    const q = linha[i];
    if (Math.abs(q[0] - p[0]) > 180) {
      const lado = p[0] > 0 ? 180 : -180;
      const qx = q[0] + (lado > 0 ? 360 : -360);
      const t = (lado - p[0]) / (qx - p[0]);
      const lat = p[1] + (q[1] - p[1]) * t;
      atual.push([lado, lat]);
      pecas.push(atual);
      atual = [[-lado, lat]];
    }
    atual.push(q);
  }
  pecas.push(atual);
  return pecas;
}

/** Uma diferença de longitudes em (−180, 180]. */
const volta = (dl) => dl - 360 * Math.round(dl / 360);
const r2 = (v) => {
  const r = Math.round(v * 100) / 100;
  return r === 0 ? 0 : r;
};

/**
 * O arco de "as duas terras" na projeção, antes da escala: os dois pontos
 * (equirretangular à volta do meio dos dois, com o fator de latitude), o arco
 * de círculo com a flecha proporcional à corda, o ponto mais alto, a direção
 * para onde curva e a caixa do arco.
 */
function arcoProjetado(a, b) {
  const lon0 = a.lon + volta(b.lon - a.lon) / 2;
  const k = Math.cos(((a.lat + b.lat) / 2) * RAD);
  // em "graus no equador": x para este, y para sul (como no papel)
  const proj = ([lon, lat]) => [volta(lon - lon0) * k, -lat];
  const pa = proj([a.lon, a.lat]);
  const pb = proj([b.lon, b.lat]);
  const dx = pb[0] - pa[0];
  const dy = pb[1] - pa[1];
  const corda = Math.hypot(dx, dy);
  // a normal da corda que aponta para cima (y < 0); numa corda vertical, para a direita
  let nx = dy / corda;
  let ny = -dx / corda;
  if (ny > 1e-9 || (Math.abs(ny) <= 1e-9 && nx < 0)) {
    nx = -nx;
    ny = -ny;
  }
  // arco de círculo pelas duas pontas, com a flecha proporcional à corda
  const flecha = FLECHA_ARCO * corda;
  const raio = (corda * corda) / 4 / (2 * flecha) + flecha / 2;
  const mx = (pa[0] + pb[0]) / 2;
  const my = (pa[1] + pb[1]) / 2;
  const cx = mx - nx * (raio - flecha);
  const cy = my - ny * (raio - flecha);
  const ta = Math.atan2(pa[1] - cy, pa[0] - cx);
  let dt = Math.atan2(pb[1] - cy, pb[0] - cx) - ta;
  dt -= 2 * Math.PI * Math.round(dt / (2 * Math.PI));
  const arco = [];
  for (let i = 0; i <= TROCOS_ARCO; i++) {
    const t = ta + (dt * i) / TROCOS_ARCO;
    arco.push([cx + raio * Math.cos(t), cy + raio * Math.sin(t)]);
  }
  arco[0] = pa;
  arco[TROCOS_ARCO] = pb;
  const apice = [mx + nx * flecha, my + ny * flecha];
  const xs = arco.map((p) => p[0]);
  const ys = arco.map((p) => p[1]);
  const bx0 = Math.min(...xs);
  const by0 = Math.min(...ys);
  const bw = Math.max(...xs) - bx0 || 1e-9;
  const bh = Math.max(...ys) - by0 || 1e-9;
  return { lon0, k, proj, arco, apice, normal: [nx, ny], bx0, by0, bw, bh };
}

/** A área da forma onde cabe o arco (a forma tirando as margens). */
function areaDoArco(F) {
  if (F.tipo === 'circulo') {
    const lado = 2 * F.raio * QUADRADO_CIRCULO;
    return { x: F.cx - lado / 2, y: F.cy - lado / 2, w: lado, h: lado };
  }
  const m = MARGENS_DUAS_TERRAS;
  return { x: m.lados, y: m.topo, w: Math.max(1, F.largura - 2 * m.lados), h: Math.max(1, F.altura - m.topo - m.baixo) };
}

/**
 * A moldura retangular de "as duas terras" para dois pontos: 120 mm de altura
 * e a largura que o arco pede com as margens, entre 160 e 240 mm. Um par
 * na diagonal fica numa moldura mais estreita, cortada à volta dos pontos, em
 * vez de mostrar costa a mais dos lados.
 */
export function formaDuasTerras(pontos) {
  const { alturaMm, larguraMinMm, larguraMaxMm } = MOLDURA_DUAS_TERRAS;
  const A = arcoProjetado(pontos[0], pontos[1]);
  const max = areaDoArco({ tipo: 'retangulo', largura: larguraMaxMm, altura: alturaMm });
  const escala = Math.min(max.w / A.bw, max.h / A.bh);
  const largura = A.bw * escala + 2 * MARGENS_DUAS_TERRAS.lados;
  const final = Math.min(larguraMaxMm, Math.max(larguraMinMm, Math.ceil(largura)));
  return { tipo: 'retangulo', larguraMm: final, alturaMm };
}

/**
 * O enquadramento de "as duas terras": a projeção dos dois pontos, o arco no
 * papel e a escala que os põe dentro da forma. Só depende dos pontos e da
 * forma, para a página, o PC e o carregador (pela caixa) verem o mesmo.
 */
function enquadramento(a, b, F) {
  const { lon0, k, proj, arco, apice, normal, bx0, by0, bw, bh } = arcoProjetado(a, b);
  const area = areaDoArco(F);
  const escala = Math.min(area.w / bw, area.h / bh);
  const ox = area.x + (area.w - bw * escala) / 2 - bx0 * escala;
  const oy = area.y + (area.h - bh * escala) / 2 - by0 * escala;
  const paraMm = (p) => [ox + p[0] * escala, oy + p[1] * escala];
  // do papel para graus (a caixa dos dados)
  const lonDe = (x) => lon0 + (x - ox) / escala / k;
  const latDe = (y) => -(y - oy) / escala;
  return { lon0, k, proj, paraMm, lonDe, latDe, arco, apice, normal };
}

/**
 * A caixa em graus (`{ oeste, este, norte, sul }`, seis casas) que a forma
 * mostra em "as duas terras": é a que o carregador pede ao OpenFreeMap. A
 * longitude segue sem dar a volta (pode passar de ±180), como `tilesDaCaixa`
 * espera.
 */
export function caixaDuasTerras(pontos, forma) {
  const F = prepararForma(forma);
  const E = enquadramento(pontos[0], pontos[1], F);
  const r6 = (v) => Math.round(v * 1e6) / 1e6;
  return { oeste: r6(E.lonDe(0)), este: r6(E.lonDe(F.largura)), norte: r6(E.latDe(0)), sul: r6(E.latDe(F.altura)) };
}

function duasTerras(pedido, F) {
  const [a, b] = pedido.pontos;
  const dist = distanciaM(a, b);
  if (dist < DISTANCIA_MINIMA_M) return { motivo: 'pontos_proximos' };
  if (Math.PI - dist / R_TERRA < 1e-4) return { motivo: 'pontos_antipodas' };
  const costaLL = pedido.dados?.costa;
  if (!Array.isArray(costaLL)) return { motivo: 'sem_dados' };

  const E = enquadramento(a, b, F);
  const lado = Math.min(F.largura, F.altura);
  const raioPonto = RAIO_PONTO * lado;

  // a costa: longitude relativa ao meridiano central, cortada no antimeridiano dele
  let costa = '';
  let trocos = 0;
  for (const linha of costaLL) {
    if (!Array.isArray(linha) || linha.length < 2) continue;
    const rel = linha.map(([lon, lat]) => [volta(lon - E.lon0), lat]);
    for (const peca of cortarNoAntimeridiano(rel)) {
      const mm = simplificar(peca.map(([dl, lat]) => E.paraMm([dl * E.k, -lat])), TOLERANCIA_COSTA_MM);
      for (const parte of recortarLinha(mm, F)) {
        let comp = 0;
        for (let i = 1; i < parte.length; i++) comp += Math.hypot(parte[i][0] - parte[i - 1][0], parte[i][1] - parte[i - 1][1]);
        if (comp < COSTA_MINIMA_MM) continue;
        costa += caminho(parte, false);
        trocos++;
      }
    }
  }

  const extremos = [E.paraMm(E.arco[0]), E.paraMm(E.arco[TROCOS_ARCO])].map((p) => p.map(r2));
  const arco = caminho(E.arco.map(E.paraMm), false);
  const circulo = (c) => caminho(poligonoCircular(c[0], c[1], raioPonto, 32), true);
  return {
    contagem: 1 + trocos,
    arco: {
      distanciaKm: Math.round(dist / 100) / 10,
      pecas: 1,
      extensaoLonGraus: Math.round(Math.abs(volta(b.lon - a.lon)) * 100) / 100,
      extremos,
      apice: E.paraMm(E.apice).map(r2),
      normal: E.normal.map((v) => Math.round(v * 1e4) / 1e4),
      raioPontoMm: r2(raioPonto),
    },
    camadas: [
      { nome: 'costa', modo: 'traco', espessuraMm: ESPESSURA_COSTA_MM, d: costa },
      { nome: 'arco', modo: 'traco', espessuraMm: Math.max(ESPESSURA_ARCO * lado, ESPESSURA_MINIMA_MM), d: arco },
      { nome: 'pontos', modo: 'cheio', d: extremos.map(circulo).join('') },
    ],
  };
}

const DESENHOS = { ruas, edificios, agua, relevo, contorno, duas_terras: duasTerras };

/** Centro aproximado de uma geometria GeoJSON (o meio da caixa), para a regra dos 85°. */
function centroDaRegiao(g) {
  const pts = poligonosDe(g).flat(2);

  if (!pts.length) return null;
  const lats = pts.map((p) => p[1]);
  return { lat: (Math.min(...lats) + Math.max(...lats)) / 2, lon: pts[0][0] };
}

/** Desenha um estilo de mapa. Ver o contrato no cabeçalho do módulo. */
export function desenharMapa(pedido) {
  const { estilo } = pedido ?? {};
  if (!ESTILOS.includes(estilo)) throw new Error(`nnadd-mapas: estilo desconhecido: ${estilo}`);
  const F = prepararForma(pedido.forma);
  const opcoes = { espessuraMinimaMm: pedido.opcoes?.espessuraMinimaMm ?? ESPESSURA_MINIMA_MM };
  const base = { estilo, larguraMm: F.largura, alturaMm: F.altura };
  const indisponivel = (motivo) => ({ ...base, disponivel: false, motivo, vazio: true, contagem: 0, camadas: [] });

  const valido = (p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lon);
  let referencias;
  if (estilo === 'duas_terras') {
    const pts = pedido.pontos ?? [];
    if (pts.length < 2 || !valido(pts[0]) || !valido(pts[1])) return indisponivel('pontos_em_falta');
    referencias = pts.slice(0, 2);
  } else if (estilo === 'contorno') {
    const c = valido(pedido.centro) ? pedido.centro : centroDaRegiao(pedido.dados?.regiao);
    referencias = c ? [c] : [];
  } else {
    if (!valido(pedido.centro)) throw new Error('nnadd-mapas: falta o centro { lat, lon }');
    referencias = [pedido.centro];
  }
  if (referencias.some((p) => Math.abs(p.lat) > LATITUDE_MAXIMA)) return indisponivel('latitude');

  const r = DESENHOS[estilo](pedido, F, opcoes);
  if (r.motivo) return indisponivel(r.motivo);
  const { cheia, ...resto } = r;
  return {
    ...base,
    disponivel: true,
    motivo: null,
    ...resto,
    vazio: r.contagem < MINIMOS[estilo] || Boolean(cheia),
  };
}

/**
 * O resultado de `desenharMapa` num SVG em milímetros, todo numa tinta
 * (KTD7). Só `<path>` (e `<clipPath>` para o contorno), sem cores além da tinta.
 */
export function mapaParaSvg(res, { tinta = '#000000', prefixoId = 'nnadd-mapa' } = {}) {
  const W = n2(res.larguraMm);
  const H = n2(res.alturaMm);
  let defs = '';
  let corpo = '';
  res.camadas.forEach((c, i) => {
    if (!c.d) return;
    let recorte = '';
    if (c.recorte) {
      const id = `${prefixoId}-r${i}`;
      defs += `<clipPath id="${id}"><path d="${c.recorte.d}" clip-rule="${c.recorte.regra}"/></clipPath>`;
      recorte = ` clip-path="url(#${id})"`;
    }
    if (c.modo === 'cheio') corpo += `<path d="${c.d}" fill="${tinta}" fill-rule="nonzero"${recorte}/>`;
    else {
      corpo += `<path d="${c.d}" fill="none" stroke="${tinta}" stroke-width="${n2(c.espessuraMm)}" stroke-linecap="round" stroke-linejoin="round"${recorte}/>`;
    }
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}">${defs ? `<defs>${defs}</defs>` : ''}${corpo}</svg>`;
}
